import { MinusCircleOutlined, PlusOutlined } from "@ant-design/icons";

import { useEffect, useState } from "react";

import { Form, Input, InputNumber, Select, Modal, Switch, ConfigProvider, Button, Checkbox } from "antd";

import { useTranslation } from "react-i18next";

import PropTypes from "prop-types";

import apiClient from "../../helpers/apiClient";
import { insuranceAddonApi } from "./lib/addonApi";
import customizeRequiredMark from "../../components/custom_antd/CustomizeRequiredMark";
import { useToast } from "../../contexts/ToastContext";
import FEATURE_FLAGS from "../../config/featureFlags";

export default function CreateInsuranceAddon({ open, onClose, addonToEdit, onSuccess }) {
    const { t } = useTranslation(["addons"]);
    const toast = useToast();
    const [form] = Form.useForm();
    const [loading, setLoading] = useState(false);
    const [isActive, setIsActive] = useState(true);
    const [selectedProvider, setSelectedProvider] = useState(addonToEdit?.provider || null);

    // Get max limit based on provider
    const getMaxLimit = (provider) => {
        if (provider === "teqMobility") return 110;
        if (provider === "bonzah") return 35;
        if (provider === "abi") return 35;
        return 110; // Default fallback
    };

    useEffect(() => {
        if (open && !addonToEdit) {
            // Initialize form for create mode - pricingRanges will be populated by backend based on coverageType
            form.setFieldsValue({
                featureList: [{}],
            });
        }
    }, [open, addonToEdit, form]);

    useEffect(() => {
        if (addonToEdit) {
            // Normalize featureList - ensure it's an array with at least one empty object if empty
            const normalizedFeatureList =
                Array.isArray(addonToEdit.featureList) && addonToEdit.featureList.length > 0
                    ? addonToEdit.featureList
                    : [{}];

            // Normalize pricingRanges - backend may populate from config if empty, but we handle empty case
            const normalizedPricingRanges = Array.isArray(addonToEdit.pricingRanges) ? addonToEdit.pricingRanges : [];

            form.setFieldsValue({
                name: addonToEdit.name,
                provider: addonToEdit.provider || undefined,
                featureList: normalizedFeatureList,
                insuranceCategory: addonToEdit?.insuranceCategory || undefined,
                coverageType: addonToEdit?.coverageType || undefined,
                pricingRanges: normalizedPricingRanges,
            });

            setIsActive(addonToEdit.status === "active");
            setSelectedProvider(addonToEdit.provider || null);
        } else {
            form.setFieldsValue({ featureList: [{}] });
        }
    }, [addonToEdit, form]);

    const handleCancel = () => {
        onClose();
        form.resetFields();
        form.setFieldsValue({ featureList: [{}] });
        setIsActive(true);
        setLoading(false);
    };

    const onFinish = async (values) => {
        // Build pricingRanges payload
        let pricingRanges = [];
        if (addonToEdit) {
            // Edit mode: validate and preserve all fields from original, only update upchargeAmount
            if (!values.pricingRanges || !Array.isArray(values.pricingRanges) || values.pricingRanges.length === 0) {
                return toast.error({ message: t("createAddons.pricingRangesRequired") });
            }

            // Validate each pricing range in edit mode
            const validationErrors = values.pricingRanges
                .map((range, index) => {
                    if (range.upchargeAmount != null && range.upchargeAmount < 0) {
                        return t("createAddons.negativeValuesError");
                    }
                    // Validate total (base + upcharge) doesn't exceed provider-specific limit
                    const originalRange = addonToEdit.pricingRanges?.[index];
                    if (originalRange) {
                        const provider = addonToEdit.provider || selectedProvider;
                        const maxLimit = getMaxLimit(provider);
                        const baseprice = originalRange.baseprice ?? 0;
                        const upchargeAmount = range.upchargeAmount != null ? Number(range.upchargeAmount) : 0;
                        const total = baseprice + upchargeAmount;
                        if (total > maxLimit) {
                            return `Total (Base + Upcharge) cannot exceed $${maxLimit}. Current total: $${total.toFixed(2)}`;
                        }
                    }
                    return null;
                })
                .filter((error) => error !== null);

            if (validationErrors.length > 0) {
                return toast.error({ message: validationErrors[0] });
            }

            // Preserve all fields from original, only update upchargeAmount
            pricingRanges = values.pricingRanges.map((range, index) => {
                const originalRange = addonToEdit.pricingRanges?.[index];
                if (originalRange) {
                    return {
                        minCarValue: originalRange.minCarValue ?? 0,
                        maxCarValue: originalRange.maxCarValue ?? null,
                        upchargeAllowed: originalRange.upchargeAllowed ?? true,
                        baseprice: originalRange.baseprice ?? 0,
                        upchargeAmount: range.upchargeAmount != null ? Number(range.upchargeAmount) : 0,
                    };
                }
                // Fallback if original range doesn't exist
                return {
                    minCarValue: Number(range.minCarValue ?? 0),
                    maxCarValue: range.maxCarValue != null ? Number(range.maxCarValue) : null,
                    upchargeAllowed: Boolean(range.upchargeAllowed ?? true),
                    baseprice: Number(range.baseprice ?? 0),
                    upchargeAmount: range.upchargeAmount != null ? Number(range.upchargeAmount) : 0,
                };
            });
        } else {
            // Create mode: pricingRanges will be empty, backend will populate from config based on coverageType
            pricingRanges = [];
        }

        setLoading(true);
        try {
            // Build payload matching backend schema structure
            const payload = addonToEdit
                ? {
                      // Edit mode: only upcharge amount changes; preserve everything else from addonToEdit
                      name: addonToEdit.name,
                      provider: addonToEdit.provider,
                      insuranceCategory: addonToEdit.insuranceCategory,
                      coverageType: addonToEdit.coverageType,
                      featureList: Array.isArray(addonToEdit.featureList)
                          ? addonToEdit.featureList.map((f) => ({
                                text: String(f?.text ?? ""),
                                isActive: Boolean(f?.isActive ?? false),
                            }))
                          : [],
                      pricingRanges,
                      status: isActive ? "active" : "inactive",
                  }
                : {
                      name: values.name,
                      provider: values.provider,
                      insuranceCategory: values.insuranceCategory,
                      coverageType: values.coverageType,
                      featureList: Array.isArray(values.featureList)
                          ? values.featureList
                                .filter((f) => f && typeof f === "object" && f.text)
                                .map((f) => ({
                                    text: String(f.text ?? ""),
                                    isActive: Boolean(f.isActive ?? false),
                                }))
                          : [],
                      pricingRanges,
                      status: isActive ? "active" : "inactive",
                  };

            let resp;
            if (addonToEdit) {
                // Update existing addon
                resp = await apiClient.patch(insuranceAddonApi.update(addonToEdit.id), payload);
            } else {
                // Create new addon
                resp = await apiClient.post(insuranceAddonApi.create(), payload);
            }

            if (resp?.data?.success) {
                toast.success({
                    message: addonToEdit ? t("addonUpdatedSuccess") : t("addonCreatedSuccess"),
                });
                onSuccess();
                handleCancel();
            }
        } catch (err) {
            if (err.response?.status === 401) {
                toast.error({ message: t("createAddons.notAuthorized") });
            } else {
                toast.error({
                    message: addonToEdit ? t("createAddons.errorUpdating") : t("createAddons.savingError"),
                });
            }
        } finally {
            setLoading(false);
        }
        return undefined;
    };

    const title = addonToEdit ? t("editInsuranceAddon") : t("createInsuranceAddon");
    const isEditUpchargeOnly = !!addonToEdit;

    return (
        <Modal
            open={open}
            onOk={() => form.submit()}
            okButtonProps={{ loading }}
            cancelButtonProps={{ disabled: loading }}
            okText={addonToEdit ? t("createAddons.updateAddon") : t("createAddons.saveAddon")}
            onCancel={handleCancel}
            closeIcon
            maskClosable={false}>
            <div className="p-6 rounded-2xl min-h-[520px] max-h-[90vh] overflow-y-auto">
                <div className="flex justify-between items-center gap-4 mb-6">
                    <div>
                        <h2 className="text-3xl font-semibold">{title}</h2>
                        {isEditUpchargeOnly && (
                            <p className="text-gray-500 text-sm mt-1">
                                {addonToEdit?.name && `${addonToEdit.name} · `}
                                {t("createAddons.updateUpchargeOnly")}
                            </p>
                        )}
                    </div>
                    <ConfigProvider theme={{ token: { colorPrimary: "#36d649" } }}>
                        <Switch
                            checkedChildren={t("createAddons.switchOn")}
                            unCheckedChildren={t("createAddons.switchOff")}
                            onChange={setIsActive}
                            checked={isActive}
                        />
                    </ConfigProvider>
                </div>

                <Form
                    layout="vertical"
                    form={form}
                    onFinish={onFinish}
                    className="max-w-xl"
                    initialValues={{ featureList: [{}] }}
                    requiredMark={customizeRequiredMark}>
                    {!isEditUpchargeOnly && (
                        <>
                            <Form.Item
                                label={t("name")}
                                name="name"
                                rules={[{ required: true, message: t("createAddons.nameRequired") }]}>
                                <Input placeholder={t("createAddons.enterName")} />
                            </Form.Item>

                            <Form.Item
                                label={t("createAddons.provider")}
                                name="provider"
                                rules={[{ required: true, message: t("createAddons.providerRequired") }]}>
                                <Select
                                    defaultValue={addonToEdit?.provider || undefined}
                                    onChange={(value) => {
                                        setSelectedProvider(value);
                                        form.setFieldsValue({ coverageType: undefined });
                                    }}
                                    options={[
                                        { label: t("createAddons.teqMobility"), value: "teqMobility" },
                                        { label: t("createAddons.bonzah"), value: "bonzah" },
                                        // INSHUR-DISABLED
                                        // { label: t("createAddons.abi"), value: "abi" },
                                    ]}
                                    placeholder={t("createAddons.selectProvider")}
                                />
                            </Form.Item>

                            <Form.Item
                                label={t("createAddons.insuranceCategory")}
                                name="insuranceCategory"
                                rules={[{ required: true, message: t("createAddons.insuranceCatError") }]}>
                                <Select
                                    defaultValue={addonToEdit?.insuranceCategory || undefined}
                                    options={[
                                        { label: t("createAddons.OFF_RENT"), value: "OFF_RENT" },
                                        { label: t("createAddons.PERSONAL"), value: "PERSONAL" },
                                        { label: t("createAddons.RIDESHARE"), value: "RIDESHARE" },
                                    ]}
                                    placeholder={t("createAddons.selectInsuranceCategory")}
                                />
                            </Form.Item>

                            <Form.Item
                                label={t("createAddons.coverageType")}
                                name="coverageType"
                                dependencies={["provider"]}
                                rules={[{ required: true, message: t("createAddons.coverageTypeRequired") }]}>
                                <Select
                                    defaultValue={addonToEdit?.coverageType || undefined}
                                    options={
                                        selectedProvider === "teqMobility" || selectedProvider === "abi"
                                            ? [
                                                  ...(FEATURE_FLAGS.teqFullCoverage
                                                      ? [
                                                            {
                                                                label: t("createAddons.FULL_COVERAGE"),
                                                                value: "FULL_COVERAGE",
                                                            },
                                                        ]
                                                      : []),
                                                  { label: t("createAddons.LIABILITY"), value: "LIABILITY" },
                                              ]
                                            : selectedProvider === "bonzah"
                                              ? [
                                                    { label: t("createAddons.CDW"), value: "CDW" },
                                                    { label: t("createAddons.RCLI"), value: "RCLI" },
                                                    { label: t("createAddons.SLI"), value: "SLI" },
                                                    { label: t("createAddons.PAI"), value: "PAI" },
                                                ]
                                              : [
                                                    ...(FEATURE_FLAGS.teqFullCoverage
                                                        ? [
                                                              {
                                                                  label: t("createAddons.FULL_COVERAGE"),
                                                                  value: "FULL_COVERAGE",
                                                              },
                                                          ]
                                                        : []),
                                                    { label: t("createAddons.LIABILITY"), value: "LIABILITY" },
                                                    { label: t("createAddons.CDW"), value: "CDW" },
                                                    { label: t("createAddons.RCLI"), value: "RCLI" },
                                                    { label: t("createAddons.SLI"), value: "SLI" },
                                                    { label: t("createAddons.PAI"), value: "PAI" },
                                                ]
                                    }
                                    placeholder={t("createAddons.selectCoverageType")}
                                />
                            </Form.Item>

                            <p>
                                Add features <span style={{ color: "red" }}>*</span>
                            </p>
                            <Form.List name="featureList">
                                {(fields, { add, remove }) => (
                                    <>
                                        {fields.map(({ key, name, ...restField }) => (
                                            <div key={key} className="flex w-full mb-4 justify-between items-center">
                                                <div className="flex items-center gap-1 w-full">
                                                    <Form.Item
                                                        {...restField}
                                                        name={[name, "isActive"]}
                                                        className="mb-0"
                                                        valuePropName="checked">
                                                        <Checkbox />
                                                    </Form.Item>
                                                    <Form.Item
                                                        {...restField}
                                                        name={[name, "text"]}
                                                        className="mb-0 w-full"
                                                        rules={[
                                                            {
                                                                required: true,
                                                                message: t("createAddons.featureRequired"),
                                                            },
                                                        ]}>
                                                        <Input placeholder="Enter feature" />
                                                    </Form.Item>
                                                </div>
                                                <div className="flex items-center justify-center">
                                                    {fields.length > 1 && (
                                                        <MinusCircleOutlined
                                                            onClick={() => remove(name)}
                                                            className="text-red-500"
                                                            style={{ fontSize: "18px" }}
                                                        />
                                                    )}
                                                </div>
                                            </div>
                                        ))}
                                        <Form.Item>
                                            <Button
                                                type="dashed"
                                                onClick={() => add()}
                                                block
                                                icon={<PlusOutlined />}
                                                className="mb-3">
                                                {t("createAddons.addMore")}
                                            </Button>
                                        </Form.Item>
                                    </>
                                )}
                            </Form.List>
                        </>
                    )}

                    {/* Pricing Ranges - Only show in edit mode if ranges exist, backend will populate in create mode */}
                    {addonToEdit && addonToEdit.pricingRanges?.length > 0 && (
                        <div className="mt-4">
                            <div className="flex justify-between items-center mb-3">
                                <p className="font-medium">{t("createAddons.pricingRanges")}</p>
                            </div>
                            <Form.List name="pricingRanges">
                                {(fields) => {
                                    // Handle empty pricingRanges - backend will populate from config, but show message
                                    if (fields.length === 0) {
                                        return (
                                            <div className="text-sm text-gray-500 italic p-4 border rounded-lg bg-gray-50">
                                                {addonToEdit?.coverageType
                                                    ? t("createAddons.pricingRangesWillBeLoaded")
                                                    : t("createAddons.noPricingRangesAvailable")}
                                            </div>
                                        );
                                    }

                                    return (
                                        <div className="space-y-4">
                                            {fields.map(({ key, name, ...restField }) => {
                                                const range = addonToEdit?.pricingRanges?.[name];
                                                const minCarValue = range?.minCarValue ?? 0;
                                                const maxCarValue = range?.maxCarValue ?? null;
                                                const baseprice = range?.baseprice ?? 0;
                                                const upchargeAllowed = range?.upchargeAllowed ?? true;
                                                const provider = addonToEdit?.provider || selectedProvider;
                                                const maxLimit = getMaxLimit(provider);

                                                return (
                                                    <div key={key} className="border rounded-lg p-4 bg-gray-50">
                                                        <div className="mb-3">
                                                            <span className="text-sm font-medium text-gray-700">
                                                                {t("createAddons.carValueRange")}:
                                                            </span>
                                                            <span className="ml-2 text-sm">
                                                                ${minCarValue.toLocaleString()} {t("createAddons.to")}{" "}
                                                                {maxCarValue != null
                                                                    ? `$${maxCarValue.toLocaleString()}`
                                                                    : t("createAddons.andAbove")}
                                                            </span>
                                                        </div>
                                                        <div className="grid grid-cols-2 gap-4">
                                                            <Form.Item
                                                                {...restField}
                                                                label={t("createAddons.basePrice")}
                                                                name={[name, "baseprice"]}>
                                                                <InputNumber
                                                                    className="w-full"
                                                                    disabled
                                                                    min={0}
                                                                    prefix="$"
                                                                    value={baseprice}
                                                                    placeholder={t("createAddons.informationOnly")}
                                                                />
                                                            </Form.Item>
                                                            <Form.Item
                                                                {...restField}
                                                                label={t("createAddons.upchargeAmount")}
                                                                name={[name, "upchargeAmount"]}
                                                                tooltip={
                                                                    upchargeAllowed
                                                                        ? t("createAddons.upchargeTooltip")
                                                                        : t("createAddons.informationOnly")
                                                                }
                                                                rules={[
                                                                    {
                                                                        validator: (_, value) => {
                                                                            if (!upchargeAllowed) {
                                                                                return Promise.resolve();
                                                                            }
                                                                            const upchargeValue = value != null ? Number(value) : 0;
                                                                            const total = baseprice + upchargeValue;
                                                                            if (total > maxLimit) {
                                                                                return Promise.reject(
                                                                                    new Error(
                                                                                        `Total (Base + Upcharge) cannot exceed $${maxLimit}. Current total: $${total.toFixed(2)}`,
                                                                                    ),
                                                                                );
                                                                            }
                                                                            return Promise.resolve();
                                                                        },
                                                                    },
                                                                ]}>
                                                                <InputNumber
                                                                    className="w-full"
                                                                    min={0}
                                                                    max={maxLimit - baseprice}
                                                                    disabled={!upchargeAllowed}
                                                                    prefix="$"
                                                                    placeholder={
                                                                        upchargeAllowed
                                                                            ? t("createAddons.enterUpcharge")
                                                                            : t("createAddons.informationOnly")
                                                                    }
                                                                />
                                                            </Form.Item>
                                                        </div>
                                                        {!upchargeAllowed && (
                                                            <p className="text-xs text-gray-500 mt-1">
                                                                {t("createAddons.informationOnly")}
                                                            </p>
                                                        )}
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    );
                                }}
                            </Form.List>
                        </div>
                    )}
                </Form>
            </div>
        </Modal>
    );
}

CreateInsuranceAddon.propTypes = {
    open: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    addonToEdit: PropTypes.object,
    onSuccess: PropTypes.func.isRequired,
};
